import { useState } from "react";
import axios from "axios";

// Self-serve ticket → vinyl + $40 refund swap page for guests whose show was
// rescheduled. Buyer authenticates via email + their ticket reference
// (MM-MEL-XXXXX / MM-ADL-XXXXX) — both are already in their inbox.

const API = process.env.REACT_APP_BACKEND_URL;

export default function TicketSwap() {
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setResult(null);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/ticket-swap/vinyl`, {
        email: email.trim(),
        ticket_reference: ref.trim().toUpperCase(),
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please email info@theprocess.studio.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0c] text-[#e7e3d8] font-[Cormorant_Garamond,Georgia,serif]" data-testid="ticket-swap-page">
      <div className="max-w-lg mx-auto px-6 py-16 sm:py-24">
        <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#8b8e9a] mb-3">Metamorph · Ticket swap</p>
        <h1 className="text-4xl sm:text-5xl mb-6 font-light text-[#f3e0a6] tracking-[0.02em]" style={{fontFamily: "'Italiana', 'Cormorant Garamond', Georgia, serif"}}>
          Swap your ticket for a vinyl.
        </h1>
        <p className="text-[14px] leading-relaxed text-[#d6d2c5] mb-10">
          For Melbourne + Adelaide guests unable to attend the rescheduled dates.
          You&rsquo;ll receive <span className="text-[#f3e0a6]">one signed vinyl copy of Metamorph</span>, plus
          a <span className="text-[#f3e0a6]">$40 refund</span> back on your original card within 5&ndash;10 business days.
        </p>

        {!result && (
          <form onSubmit={submit} className="space-y-5" data-testid="ticket-swap-form">
            <div>
              <label className="block text-[10.5px] tracking-[0.24em] uppercase text-[#aab09e] mb-2">Your email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="ticket-swap-email-input"
                className="w-full bg-transparent border-b border-[#3a3a3a] focus:border-[#f3e0a6] outline-none py-3 text-[16px] text-[#e7e3d8] placeholder-[#5a5a5a]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-[10.5px] tracking-[0.24em] uppercase text-[#aab09e] mb-2">Ticket reference</label>
              <input
                type="text"
                value={ref}
                onChange={(e) => setRef(e.target.value.toUpperCase())}
                required
                spellCheck={false}
                data-testid="ticket-swap-ref-input"
                className="w-full bg-transparent border-b border-[#3a3a3a] focus:border-[#f3e0a6] outline-none py-3 text-[16px] tracking-[0.18em] text-[#e7e3d8] placeholder-[#5a5a5a] uppercase"
                placeholder="MM-MEL-XXXXX or MM-ADL-XXXXX"
              />
              <p className="text-[11px] italic text-[#7a7f6e] mt-2">
                Find this on your PDF ticket (top of the page, next to the QR).
              </p>
            </div>

            {error && (
              <p className="text-[13px] text-[#f3a6a6] italic border-l-2 border-[#f3a6a6] pl-3 py-1" data-testid="ticket-swap-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email || !ref}
              data-testid="ticket-swap-submit"
              className="mt-8 inline-block tracking-[0.22em] uppercase text-[11px] text-[#f3e0a6] border border-[#f3e0a6]/60 hover:bg-[#f3e0a6]/10 py-4 px-8 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Processing…" : "Confirm swap"}
            </button>
            <p className="text-[11px] italic text-[#7a7f6e] mt-4">
              One click, one confirmation. Refunds settle in 5&ndash;10 business days.
            </p>
          </form>
        )}

        {result && (
          <div data-testid="ticket-swap-success" className="space-y-6">
            <div className="border-t border-[#f3e0a6]/20 pt-8">
              <p className="text-[10.5px] tracking-[0.24em] uppercase text-[#f3e0a6]">
                {result.already_swapped ? "Already swapped" : "Swap confirmed"}
              </p>
              <h2 className="text-3xl mt-4 mb-4 font-light text-[#f3e0a6]" style={{fontFamily: "'Italiana', serif"}}>
                Thank you.
              </h2>
              <p className="text-[14px] leading-relaxed text-[#d6d2c5]">
                {result.message}
              </p>
              {result.refund_id && (
                <p className="text-[11px] mt-6 tracking-[0.18em] uppercase text-[#7a7f6e]">
                  Stripe ref · <span className="text-[#d6d2c5]">{result.refund_id}</span>
                </p>
              )}
              <p className="text-[11px] italic text-[#7a7f6e] mt-6">
                A confirmation has been sent to your inbox. Any questions, reply to that email.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
